import { Badge } from "@package/subframe/components/Badge";
import { IconButton } from "@package/subframe/components/IconButton";
import { FeatherArrowLeft, FeatherCheck } from "@subframe/core";

import {
  CompletionRecordDetails,
  CompletionRecordFacts,
} from "@/components/compliance/completion-record/completion-record";
import type {
  ComplianceTaskDefinitionResponseDto,
  ComplianceTaskInstanceResponseDto,
} from "@/generated/model";

interface Props {
  instance: ComplianceTaskInstanceResponseDto;
  definition: ComplianceTaskDefinitionResponseDto;
  onBack: () => void;
  /**
   * True when the instance came from the offline cache, whose signed evidence
   * URLs have expired. Evidence rows render inert in that case.
   */
  isServedFromCache?: boolean;
}

/**
 * Read-only view of an already-completed compliance task instance. Shown when
 * the practitioner navigates to an instance that's already in COMPLETED status —
 * either because they completed it themselves earlier, or another user did.
 */
export function CompletionSummary({
  instance,
  definition,
  onBack,
  isServedFromCache = false,
}: Props) {
  return (
    <div className="flex w-full flex-col items-start bg-white animate-fade-in">
      <div className="flex w-full flex-col items-start gap-1 pb-6">
        <div className="flex w-full items-center gap-2">
          <IconButton
            variant="neutral-secondary"
            icon={<FeatherArrowLeft />}
            aria-label="Back"
            onClick={onBack}
          />
          <span className="grow shrink-0 basis-0 text-metrics font-metrics text-primary-navy-800">
            {definition.name}
          </span>
          <Badge variant="success" icon={<FeatherCheck />}>
            Completed
          </Badge>
        </div>
        {definition.description && (
          <span className="pl-12 text-body font-body text-subtext-color">
            {definition.description}
          </span>
        )}
      </div>

      <div className="flex w-full max-w-2xl mx-auto flex-col items-start gap-6 pb-12">
        <CompletionRecordFacts instance={instance} />
        <CompletionRecordDetails
          instance={instance}
          definition={definition}
          evidenceLinks={isServedFromCache ? "offline" : "live"}
        />
      </div>
    </div>
  );
}
